import { type GetStaticPaths, type GetStaticProps } from 'next';
import getGameSlugs from '../../helpers/getGameSlugs';
import configureSentryScope from '../../helpers/configureSentryScope';
import getGamePageViewModels from '../../helpers/getGamePageViewModels';
import GamePage, {
  type GamePageProps,
} from '../../components/GamePage/GamePage';
import buildComponentViewModelsFromEnhancedMarkdowns from '../../helpers/buildComponentViewModelsFromEnhancedMarkdowns';
import { getPlaiceholder } from 'plaiceholder';
import convertImageUrlToS3ImageUrl from '../../helpers/convertImageUrlToS3ImageUrl';
import useViewModels, {
  type ViewModels,
  ViewModelsProvider,
} from '../../contexts/useViewModels';
import React, { useEffect, type JSX } from 'react';
import { initRequest } from '../../helpers/rpgLogsApiRequestMetadata';

export const GamePagePath = '/[gameSlug]';

function InnerPage({
  viewModels,
  gameProps,
}: {
  viewModels: ViewModels;
  gameProps: GamePageProps;
}) {
  const { setViewModels } = useViewModels();
  useEffect(() => {
    setViewModels(viewModels);
  }, [viewModels, setViewModels]);

  return <GamePage {...gameProps} />;
}

export default function OuterGamePage({
  viewModels,
  ...props
}: {
  viewModels: ViewModels;
} & GamePageProps): JSX.Element {
  return (
    <ViewModelsProvider value={viewModels}>
      <InnerPage viewModels={viewModels} gameProps={props} />
    </ViewModelsProvider>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const gameSlugs = [
    ...(await getGameSlugs()),
    'classic-wrath',
    'classic-cata',
  ];

  return {
    paths: gameSlugs.map((gameSlug) => ({
      params: {
        gameSlug,
      },
    })),
    fallback: false,
  };
};

const thirteenHours = 13 * 60 * 60;
export const getStaticProps: GetStaticProps = async ({ params }) => {
  configureSentryScope(params);

  if (!params?.gameSlug || typeof params.gameSlug !== 'string') {
    return {
      notFound: true,
    };
  }

  if (
    params.gameSlug === 'classic-wrath' ||
    params.gameSlug === 'classic-cata'
  ) {
    return {
      props: {
        redirect: {
          destination: '/classic-mop',
        },
      },
      revalidate: thirteenHours,
    };
  }

  initRequest({
    route: '/[gameSlug]',
    path: `/${params.gameSlug}`,
  });

  const pageViewModels = await getGamePageViewModels(params.gameSlug);

  if (!pageViewModels.gamePage) {
    return {
      notFound: true,
    };
  }

  const componentViewModels =
    await buildComponentViewModelsFromEnhancedMarkdowns(
      [
        ...pageViewModels.gamePage.resources.map(
          (resource) => `<Image src="${resource.imageUrl}" />`,
        ),
        ...(pageViewModels.gamePage.builds?.highlights?.map(
          (highlight) => `<Image src="${highlight.highlightImageUrl}" />`,
        ) ?? []),
      ],
      {
        getPlaiceholder: async (src: string) =>
          await getPlaiceholder(convertImageUrlToS3ImageUrl(src)),
      },
    );

  return {
    props: {
      googleAnalytics: pageViewModels.googleAnalytics,
      game: pageViewModels.game,
      header: pageViewModels.header,
      footer: pageViewModels.footer,
      page: pageViewModels.gamePage,
      viewModels: componentViewModels,
    },
    revalidate: thirteenHours,
  };
};
